import * as Yup from 'yup';
import { endpoints } from 'src/utils/axios';

export const localityConfig = {
    // Entity metadata
    entity: {
        name: 'Locality/Village',
        namePlural: 'Localities / Villages',
        apiEndpoint: endpoints.localityMaster,
        storageKey: 'locality-master-list-settings',
        rights: { module: 'master', menu: 'jurisdiction', submenu: 'locality' },
    },

    // Data fetching configuration
    fetch: {
        listFields: ['locality_name', 'block', 'city', 'state', 'country', 'status', 'createdAt', 'updatedAt'],
        listPopulate: ['block', 'city', 'state', 'country'],
    },

    // API endpoints
    endpoints: {
        list: endpoints.localityMaster.list,
        create: endpoints.localityMaster.create,
        update: (id) => endpoints.localityMaster.update(id),
        delete: (id) => endpoints.localityMaster.delete(id),
        getById: (id) => endpoints.localityMaster.details(id),
    },

    // Form configuration
    form: {
        title: {
            create: 'Create New Locality/Village',
            edit: 'Edit Locality/Village',
        },
        fields: [
            {
                name: 'state',
                label: 'State',
                type: 'autocomplete',
                apiEndpoint: `${endpoints.stateMaster.list}?status=active&limit=0`,
                optionLabel: 'state_name',
                optionValue: '_id',
                placeholder: 'Select state',
                validation: Yup.mixed().required('State is required'),
                gridProps: { xs: 12, sm: 6 },
            },
            {
                name: 'city',
                label: 'City',
                type: 'autocomplete',
                apiEndpoint: (formData) => {
                    const { state } = formData || {};
                    if (!state) return null;
                    const stateId = state._id || state.id || state;
                    return `${endpoints.cityMaster.list}?status=active&limit=0&state=${stateId}`;
                },
                optionLabel: 'city_name',
                optionValue: '_id',
                placeholder: 'Select city',
                validation: Yup.mixed().required('City is required'),
                gridProps: { xs: 12, sm: 6 },
                dependsOn: 'state',
            },
            {
                name: 'block',
                label: 'Block',
                type: 'autocomplete',
                apiEndpoint: (formData) => {
                    const { city } = formData || {};
                    if (!city) return null;
                    const cityId = city._id || city.id || city;
                    return `${endpoints.blockMaster.list}?status=active&limit=0&city=${cityId}`;
                },
                optionLabel: 'block_name',
                optionValue: '_id',
                placeholder: 'Select block',
                validation: Yup.mixed().required('Block is required'),
                gridProps: { xs: 12 },
                dependsOn: 'city',
            },
            {
                name: 'locality_name',
                label: 'Locality/Village Name',
                type: 'text',
                placeholder: 'Enter locality or village name',
                validation: Yup.string()
                    .required('Locality/Village name is required')
                    .min(2, 'Name must be at least 2 characters'),
                gridProps: { xs: 12 },
            },
            {
                name: 'status',
                label: 'Status',
                type: 'select',
                defaultValue: 'active',
                validation: Yup.string().required('Status is required'),
                options: [
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                ],
                gridProps: { xs: 12 },
            },
        ],
        showAvatar: false,
        maxWidth: 'sm',
    },

    // Transform payload before submit
    transformPayload: (formData) => {
        const stateValue = formData.state?.id || formData.state?._id || formData.state;
        const cityValue = formData.city?.id || formData.city?._id || formData.city;
        const blockValue = formData.block?.id || formData.block?._id || formData.block;
        return {
            ...formData,
            state: stateValue,
            city: cityValue,
            block: blockValue,
        };
    },

    // Normalize data for form (Pre-fill objects for Autocomplete)
    normalizeDataForForm: (item) => {
        if (!item) return item;
        return {
            ...item,
            state: item.state,
            city: item.city,
            block: item.block,
        };
    },

    // Table configuration
    table: {
        columns: [
            {
                id: 'locality_name',
                label: 'Locality/Village Name',
                width: 220,
                sortable: true,
                searchable: true,
                type: 'text',
            },
            {
                id: 'block',
                label: 'Block',
                width: 180,
                sortable: true,
                type: 'text',
                render: (value) => value?.block_name || '-',
            },
            {
                id: 'city',
                label: 'City',
                width: 180,
                sortable: true,
                type: 'text',
                render: (value) => value?.city_name || '-',
            },
            {
                id: 'state',
                label: 'State',
                width: 180,
                sortable: true,
                type: 'text',
                render: (value) => value?.state_name || '-',
            },
            {
                id: 'status',
                label: 'Status',
                width: 120,
                type: 'status',
                sortable: true,
                align: 'center',
                render: (value) => ({
                    label: value === 'active' ? 'Active' : 'Inactive',
                    color: value === 'active' ? 'success' : 'error',
                }),
            },
            {
                id: 'createdAt',
                label: 'Created At',
                width: 150,
                type: 'date',
                sortable: true,
                format: 'dd MMM yyyy',
            },
        ],
        defaultSort: { field: 'createdAt', direction: 'desc' },
        rowsPerPageOptions: [5, 10, 25, 50],
        defaultRowsPerPage: 10,
    },

    // Filter configuration
    filters: {
        default: {
            search: '',
            status: 'all',
            state: '',
            city: '',
            block: '',
        },
        fields: [
            {
                name: 'search',
                label: 'Search',
                type: 'search',
                placeholder: 'Search localities...',
                apiParam: 'search',
            },
            {
                name: 'status',
                label: 'Status',
                type: 'tabs',
                options: [
                    { value: 'all', label: 'All', color: 'default' },
                    { value: 'active', label: 'Active', color: 'success' },
                    { value: 'inactive', label: 'Inactive', color: 'error' },
                ],
                apiParam: 'status',
                showCount: true,
            },
            {
                name: 'state',
                label: 'State',
                type: 'autocomplete',
                apiEndpoint: `${endpoints.stateMaster.list}?status=active&limit=0`,
                optionLabel: 'state_name',
                optionValue: '_id',
                optionLabelSecondary: 'country.country_name',
                apiParam: 'state',
                placeholder: 'Filter by state',
                inAdvancedFilters: true,
            },
            {
                name: 'city',
                label: 'City',
                type: 'autocomplete',
                apiParam: 'city',
                apiEndpoint: (formData) => {
                    const { state } = formData || {};
                    if (!state || state === 'all') return `${endpoints.cityMaster.list}?status=active&limit=0`;
                    const stateId = state._id || state.id || state;
                    return `${endpoints.cityMaster.list}?status=active&limit=0&state=${stateId}`;
                },
                optionLabel: 'city_name',
                optionValue: '_id',
                dependsOn: 'state',
                placeholder: 'Filter by city',
                inAdvancedFilters: true,
            },
            {
                name: 'block',
                label: 'Block',
                type: 'autocomplete',
                apiParam: 'block',
                apiEndpoint: (formData) => {
                    const { city } = formData || {};
                    if (!city || city === 'all') return `${endpoints.blockMaster.list}?status=active&limit=0`;
                    const cityId = city._id || city.id || city;
                    return `${endpoints.blockMaster.list}?status=active&limit=0&city=${cityId}`;
                },
                optionLabel: 'block_name',
                optionValue: '_id',
                dependsOn: 'city',
                placeholder: 'Filter by block',
                inAdvancedFilters: true,
            },
        ],
    },

    // Card view configuration
    card: {
        title: (item) => item.locality_name,
        subtitle: (item) => `${item.block?.block_name || '-'}, ${item.city?.city_name || '-'}`,
        avatar: false,
        status: (item) => ({
            label: item.status === 'active' ? 'Active' : 'Inactive',
            color: item.status === 'active' ? 'success' : 'error',
        }),
        metadata: [
            {
                label: 'Block',
                value: (item) => item.block?.block_name || '-',
                icon: 'solar:widget-bold-duotone',
            },
            {
                label: 'City',
                value: (item) => item.city?.city_name || '-',
                icon: 'solar:city-bold-duotone',
            },
            {
                label: 'State',
                value: (item) => item.state?.state_name || '-',
                icon: 'solar:map-point-bold-duotone',
            },
        ],
    },

    // Permissions & features
    features: {
        create: true,
        edit: true,
        delete: true,
        bulkDelete: true,
        duplicate: true,
        export: false,
        import: false,
        viewToggle: true,
        columnVisibility: true,
    },
};
